import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BidOutcome } from '@/types/bidHistory';
import { SELECTION_REASONS } from '@/lib/constants/awardFeedback';

interface SelectionReasonsBadgesProps {
  reasons: string[] | null;
  outcome: BidOutcome;
  additionalComments?: string | null;
  maxVisible?: number;
}

// Get description for a reason - reasons are stored as labels in the database
const getReasonDescription = (reason: string): string | undefined => {
  const reasonConfig = SELECTION_REASONS.find(r => r.label === reason);
  return reasonConfig?.description;
};

export function SelectionReasonsBadges({ 
  reasons, 
  outcome, 
  additionalComments,
  maxVisible = 3 
}: SelectionReasonsBadgesProps) {
  if (!reasons || reasons.length === 0) {
    if (outcome === 'pending') {
      return <span className="text-muted-foreground text-sm">Awaiting decision</span>;
    }
    if (outcome === 'won' || outcome === 'lost') {
      return <span className="text-muted-foreground text-sm">No feedback provided</span>;
    }
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  const visibleReasons = reasons.slice(0, maxVisible);
  const hiddenCount = reasons.length - maxVisible;

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-1">
        {outcome === 'lost' && (
          <span className="text-xs text-muted-foreground">Winner selected for:</span>
        )}
        <div className="flex flex-wrap gap-1">
          {visibleReasons.map((reason, index) => {
            const description = getReasonDescription(reason);
            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <Badge 
                    variant="secondary" 
                    className={`text-xs ${
                      outcome === 'won' 
                        ? 'bg-green-500/10 text-green-700 border-green-500/20' 
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {reason}
                  </Badge>
                </TooltipTrigger>
                {(description || additionalComments) && (
                  <TooltipContent>
                    <div className="max-w-xs space-y-1">
                      {description && <p className="font-medium">{description}</p>}
                      {additionalComments && (
                        <p className="text-sm italic text-muted-foreground">{additionalComments}</p>
                      )}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
          {hiddenCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-xs">
                  +{hiddenCount} more
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-col gap-1">
                  {reasons.slice(maxVisible).map((reason, index) => (
                    <span key={index}>{reason}</span>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
